<script lang="ts">
    // Shared Component
    import ProductLockup from '@amp/web-app-components/src/components/lockups/ProductLockup/ProductLockup.svelte';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import StaticPlayButton from '~/components/jet/buttons/StaticPlayButton.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';

    // Types
    import type { ActionModel } from '~/jet/models';

    // Stores
    import { getI18n } from '~/stores/i18n';

    // Utils
    import type { Lockup } from '@jet-app/music-classical/models/section/Lockup';
    import type { SubtitleLink } from '@jet-app/music-classical/models/header';
    import { extractActionHref } from '~/jet/utils';
    import {
        handleClickAction,
        isCompoundExternalAction,
        isExternalAction,
    } from '~/actions/click-action';

    export let item: Lockup;
    const i18n = getI18n();

    // Constant Properties
    const itemProfile = 'product-rectangle';

    // Derived Properties
    $: artwork = item?.artwork;
    $: title = item?.title;
    $: url = item?.url;
    $: subtitleLinks = item?.subtitleLinks;
    $: playAction = item?.playAction;
    $: immediatePlayModule =
        item?.type === 'station' && item?.playAction !== undefined;

    // Accessible label passed to Play button (with "Explicit" prefix if needed)
    $: ariaPlayButtonText = title
        ? $i18n.t('ASE.Web.MusicClassical.VO.PlayLabel', { content: title })
        : $i18n.t('ASE.Web.MusicClassical.Play');

    // Used on charts pages for album ranking
    $: ordinal = item?.ordinal;

    $: handleWrapperClick = (e: CustomEvent<{ event: MouseEvent }>) => {
        // TODO: rdar://157049993 ([Classical on Web] - Editorial Stations: Station Page/Route)
        //              Item?.playAction added to allow mobile devices to direct play station lockups.
        //              Remove once station routes are added as play action for mobile will happen on
        //              the page like it does for Music.
        handleClickAction(e?.detail?.event, item?.segue || item?.playAction);
    };

    const deriveLinkAction = (action: ActionModel | null) => {
        return {
            url: extractActionHref(action) ?? '#',
            isExternal:
                isExternalAction(action) || isCompoundExternalAction(action),
        };
    };

    function handleTitleClick(e: CustomEvent<{ event: MouseEvent }>): void {
        handleClickAction(e?.detail?.event, item.segue);
    }

    function handleSubtitleClick(
        e: CustomEvent<{ event: MouseEvent; link: SubtitleLink }>,
    ): void {
        handleClickAction(
            e?.detail?.event,
            e?.detail?.link?.segue || item.segue,
        );
    }
</script>

<div class="product-lockup-container">
    <ProductLockup
        artwork="slot"
        {title}
        {subtitleLinks}
        ariaLabel=""
        titleHref={url}
        {ordinal}
        translateFn={$i18n.t}
        isExplicit={false}
        explicitAriaText={$i18n.t('ASE.Web.MusicClassical.Explicit')}
        contextMenuIsOpen={false}
        linkAction={deriveLinkAction(item?.segue)}
        {immediatePlayModule}
        on:wrapperClick={handleWrapperClick}
        on:titleClick={handleTitleClick}
        on:subtitleClick={handleSubtitleClick}
    >
        <svelte:fragment slot="artwork" let:alt>
            <!-- TODO: rdar://88405426 (Investigate better pattern for app <Artwork> from shared component) -->
            <Artwork {artwork} profile={itemProfile} {alt} />
        </svelte:fragment>
        <!--
        <svelte:fragment slot="favorited-badge">
            <FavoriteBadge {inFavorites} />
        </svelte:fragment> -->

        <svelte:fragment slot="play-button">
            {#if playAction}
                <StaticPlayButton
                    {playAction}
                    ariaLabel={ariaPlayButtonText}
                    isLarge={immediatePlayModule}
                />
            {/if}
        </svelte:fragment>

        <!-- <div class="square-lockup__social" slot="artwork-metadata-overlay">
            <SocialBadges contentDescriptor={item.contentDescriptor} />
        </div> -->

        <div slot="context-button">
            {#if item?.contextMenuAction}
                <ProductContextMenu
                    contextMenuAction={item?.contextMenuAction}
                />
            {/if}
        </div>
    </ProductLockup>
</div>

<style lang="scss">
    .product-lockup-container {
        display: contents;

        :global(.product-lockup__ordinal) {
            font: var(--title-1-emphasized-serif);
            margin-bottom: 2px;
        }
    }
</style>
